import { call, put, select, takeEvery, take } from 'redux-saga/effects'
import i18next from 'i18next'
import Currency from '../../Utils/Currency'
import { PLAY_DONE, ERROR_CLOSED, JACKPOT_MESSAGE_CLOSED, ADD_ENABLE_QUICKSPIN, SHOW_MESSAGE } from '../../redux/constants'
import { playRequest, message } from '../../redux/actions'
import { SET_SPINS, STOP_AUTOPLAY, UPDATE_AUTOPLAY_BALANCE, SET_BALANCE, RUN_AUTOPLAY } from './constants'
import { autoPlayDone } from './actions'
import gameDelay from '../../Utils/gameDelay'
import { timeUntilExpiration } from '../../Utils'

/**
 * Creates a list of sagas that should be run on specific events.
 */
export default function* watchAutoPlay() {
  yield takeEvery(PLAY_DONE, playDone)
  yield takeEvery(RUN_AUTOPLAY, runAutoPlay)
  yield takeEvery(UPDATE_AUTOPLAY_BALANCE, updateAutoplayBalance)
  yield takeEvery(JACKPOT_MESSAGE_CLOSED, onErrorClosed)
  yield takeEvery(ERROR_CLOSED, onErrorClosed)
}


/**
 * Stops autoplay, by setting autoplay spins to 0 when an error is closed.
 */
export function* onErrorClosed() {
  yield put({
    type: STOP_AUTOPLAY
  })
}

/**
 * This saga will run PLAY_DONE event has been dispatched.
 * It will check if any limits (win/loss/singlewin) criterias have been met,
 * otherwise it will request a new spin after a delay.
 */
export function* playDone() {
  let { autoPlay } = yield select()
  const { base, session } = yield select()
  const { response, messages } = base
  const { lossLimit, winLimit, singleWinLimit, autoplayBalance } = autoPlay

  const freeRounds = base.response && base.response.stats && base.response.stats.freespins
  if (freeRounds) {
    const expiryTime = timeUntilExpiration(freeRounds.expires)
    if ((freeRounds.spinsended || expiryTime <= 0) || !freeRounds.autoplay || (!freeRounds.spinsended && !base.displayFreeRound)) {
      yield put({
        type: STOP_AUTOPLAY
      })

      yield put(autoPlayDone())
      return
    }
  }

  if (autoPlay.spinsLeft > 0) {
    if (lossLimit || winLimit || singleWinLimit) {
      const { betAmount } = session
      const { stats } = response
      const balanceDiff = stats.b - autoplayBalance

      const limits = {}
      limits.lossLimit = lossLimit && (balanceDiff - betAmount) < (-lossLimit) ? lossLimit : false
      limits.winLimit = winLimit && balanceDiff >= winLimit ? winLimit : false
      limits.singleWinLimit = singleWinLimit && response.win >= singleWinLimit ? singleWinLimit : false

      if (limits.lossLimit || limits.winLimit || limits.singleWinLimit) {
        const messages = {}
        messages.msgs = []
        Object.keys(limits).forEach(l => {
          if (limits[l]) {
            messages.msgs.push({
              message: i18next.t('autoplay.stop', {
                limitType: i18next.t(`settings.autoplay.${l}`),
                value: Currency.format(limits[l])
              })
            })
          }
        })

        yield put({
          type: STOP_AUTOPLAY
        })

        yield put(message(messages.msgs[0].message))
        yield put(autoPlayDone())
        return
      }
    }

    // Wait for any messages to be closed, to prevent autoplay from running and triggering a new play request and get new stats before messages are closed.
    if (messages && messages.length > 0) {
      yield take(a => a.type === SHOW_MESSAGE && a.message === null)
      const { displayFreeRound } = yield select(state => state.base)
      // Stop autoplay, if player chooses to play free rounds.
      if (displayFreeRound) {
        yield put({
          type: STOP_AUTOPLAY
        })

        yield put(autoPlayDone())
        return
      }
    }
    yield gameDelay(10)

    yield put(playRequest({ ga: 'spin' }))

    yield put({
      type: SET_SPINS,
      spins: autoPlay.spinsLeft - 1
    })
  } else if (autoPlay.autoPlaying) {
    yield put(autoPlayDone())
  }
}

/**
 * This saga will start the autoplay feature.
 */
export function* runAutoPlay() {
  const { autoPlay, base } = yield select()
  const { playing, enableQuickspinCounter } = base

  yield put({ type: ADD_ENABLE_QUICKSPIN, add: -enableQuickspinCounter })

  if (!playing) {
    const spins = autoPlay.spinsLeft
    yield put({
      type: SET_SPINS,
      spins: spins - 1
    })

    yield put(playRequest({ ga: 'spin' }))
  }
}

/**
 * This saga will update the autoplay balance, which is used
 * to keep track of the amount of money earned/lost during autoplay feature.
 */
export function* updateAutoplayBalance() {
  const { session } = yield select()
  const { balance } = session

  yield put({
    type: SET_BALANCE,
    balance
  })
}
